import React, { useState } from "react";
import { motion } from "framer-motion";
import { Menu, X, Mail, Phone, MessageCircle, Facebook, Instagram, MapPin, Heart, Sparkles, PartyPopper, Gift, ArrowRight, Star as StarIcon, Search, MailOpen, Smile, HelpCircle } from "lucide-react";
import { Balloon, Confetti, Star } from "./components/Balloon";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "./components/ui/accordion";
import "./App.css";

// Config
const WHATSAPP = "+447513102329";
const WHATSAPP_LINK = `https://wa.me/${WHATSAPP.replace(/\D/g, "")}`;
const EMAIL = "kateskreations.kk@gmail.com";
const MAILTO = `mailto:${EMAIL}?subject=Enquiry%20for%20Kate's%20Kreations`;
const PHONE_DISPLAY = "07513 102329";

const GALLERY = [
  // Balloon arrangements (user provided)
  { url: "https://customer-assets.emergentagent.com/job_a8585234-ecbc-4d89-b239-c4d802920f32/artifacts/vzdnb2nm_524955784_599160919933034_7299482916164725476_n.jpg", alt: "Light-up 30 with pink & black balloon garland", cat: "balloons" },
  { url: "https://customer-assets.emergentagent.com/job_a8585234-ecbc-4d89-b239-c4d802920f32/artifacts/o3o2eyej_525214620_599161003266359_5745739587203058590_n.jpg", alt: "Light-up 40 with copper & black balloons", cat: "balloons" },
  { url: "https://customer-assets.emergentagent.com/job_a8585234-ecbc-4d89-b239-c4d802920f32/artifacts/ror4z1vy_555557721_647140051801787_1647561205983321117_n.jpg", alt: "Debbie's 60th personalised welcome sign with gold balloons", cat: "balloons" },
  { url: "https://customer-assets.emergentagent.com/job_a8585234-ecbc-4d89-b239-c4d802920f32/artifacts/3fd28am9_557222638_647140048468454_2136144265132449266_n.jpg", alt: "Light-up 60 with black & gold balloon garland", cat: "balloons" },
  { url: "https://customer-assets.emergentagent.com/job_a8585234-ecbc-4d89-b239-c4d802920f32/artifacts/3seqjh60_693222634_825057004010090_1113541061954998873_n.jpg", alt: "Light-up 40 with pink & green balloons", cat: "balloons" },
  // Personalised printing (user provided)
  { url: "https://customer-assets.emergentagent.com/job_party-perfection-3/artifacts/yqfovff9_600996538_710448602137598_2611075841015055582_n.jpg", alt: "Personalised reindeer Christmas glass cups — Kylo & Harlow", cat: "printing" },
  { url: "https://customer-assets.emergentagent.com/job_party-perfection-3/artifacts/z90s0h3g_600243206_710448175470974_2815216194968652249_n.jpg", alt: "Personalised name water bottles for a sports team", cat: "printing" },
  { url: "https://customer-assets.emergentagent.com/job_party-perfection-3/artifacts/1922f9ia_473794240_455655157616945_2040045371197354974_n.jpg", alt: "Personalised purple gift hamper for Raveen", cat: "printing" },
  { url: "https://customer-assets.emergentagent.com/job_party-perfection-3/artifacts/5i4gp88z_585313672_687794997736292_7824542433865561914_n.jpg", alt: "Festive Christmas character gift boxes — Santa, Snowman, Reindeer, Elf", cat: "printing" },
  { url: "https://customer-assets.emergentagent.com/job_party-perfection-3/artifacts/nqzp8z4i_600317760_710448192137639_5769209351133807706_n.jpg", alt: "Personalised initials and numbers on team gloves", cat: "printing" },
];

const HERO_BALLOONS = "https://static.prod-images.emergentagent.com/jobs/a8585234-ecbc-4d89-b239-c4d802920f32/images/cffd4203fc3ba1dd0277a6cba26da3ae8d7287552949f9007af8570a65674c30.png";
const KATE_PHOTO = "https://customer-assets.emergentagent.com/job_party-perfection-3/artifacts/avhwpscf_IMG_2253.JPG";
const LOGO = "https://customer-assets.emergentagent.com/job_party-perfection-3/artifacts/dmzozs3v_KK.png";

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.7, ease: "easeOut" } },
};

// ----------------- Header -----------------
const Header = () => {
  const [open, setOpen] = useState(false);

  const links = [
    { href: "#about", label: "About" },
    { href: "#services", label: "Services" },
    { href: "#gallery", label: "Gallery" },
    { href: "#testimonials", label: "Reviews" },
    { href: "#order", label: "How to Order" },
    { href: "#faq", label: "FAQ" },
    { href: "#contact", label: "Contact" },
  ];

  return (
    <header
      data-testid="site-header"
      className="relative z-40 bg-[#FFFBF9]"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-2 flex items-center justify-between">
        <a href="#top" data-testid="logo-link" className="flex items-center gap-2 group">
          <img
            src={LOGO}
            alt="Kate's Kreations — Unique and Personalised Gifts"
            data-testid="header-logo"
            className="h-56 md:h-72 lg:h-[22rem] w-auto transition-transform duration-300 group-hover:scale-105"
          />
        </a>

        <nav className="hidden lg:flex items-center gap-8">
          {links.map((l) => (
            <a
              key={l.href}
              href={l.href}
              data-testid={`nav-${l.label.toLowerCase().replace(/\s/g, "-")}`}
              className="text-[#2D2327] font-medium hover:text-[#E65084] transition-colors"
            >
              {l.label}
            </a>
          ))}
          <a href="#contact" data-testid="header-cta" className="btn-primary !py-2.5 !px-5 text-sm">
            Get in Touch <ArrowRight size={16} />
          </a>
        </nav>

        <button
          data-testid="mobile-menu-toggle"
          aria-label="Toggle menu"
          className="lg:hidden p-2 rounded-full bg-white/80 border border-pink-100"
          onClick={() => setOpen((o) => !o)}
        >
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      {open && (
        <div data-testid="mobile-menu" className="lg:hidden bg-white border-t border-pink-100 px-6 py-6 space-y-4 shadow-lg">
          {links.map((l) => (
            <a
              key={l.href}
              href={l.href}
              onClick={() => setOpen(false)}
              data-testid={`mobile-nav-${l.label.toLowerCase().replace(/\s/g, "-")}`}
              className="block text-[#2D2327] font-medium text-lg"
            >
              {l.label}
            </a>
          ))}
          <a href="#contact" onClick={() => setOpen(false)} data-testid="mobile-header-cta" className="btn-primary !py-2.5 !px-5 text-sm w-full justify-center">
            Get in Touch <ArrowRight size={16} />
          </a>
        </div>
      )}
    </header>
  );
};

// ----------------- Hero -----------------
const Hero = () => {
  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative overflow-hidden pt-8 pb-24 md:pt-12 md:pb-32"
      style={{
        background:
          "radial-gradient(ellipse at top left, #FDF2F8 0%, #FFFBF9 50%, #FFF8F0 100%)",
      }}
    >
      {/* floating decorations */}
      <Balloon color="#FFC857" highlight="#FFE9B6" size={70} className="absolute left-6 top-32 float-slow opacity-90 hidden md:block" />
      <Balloon color="#E899AE" highlight="#FCE0E8" size={56} className="absolute left-[8%] bottom-24 float-fast opacity-90 hidden md:block" />
      <Confetti color="#FF729F" size={28} className="absolute right-[12%] top-28 drift hidden md:block" />
      <Star color="#FFC857" size={26} className="absolute right-[6%] bottom-32 confetti-spin hidden md:block" />
      <Confetti color="#FFC857" size={22} className="absolute left-1/3 top-24 drift hidden md:block" />

      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16 grid lg:grid-cols-12 gap-12 items-center relative">
        {/* Text */}
        <motion.div
          initial="hidden"
          animate="visible"
          variants={fadeUp}
          className="lg:col-span-7 relative z-10"
        >
          <div className="inline-flex items-center gap-2 bg-white/80 backdrop-blur border border-pink-100 px-4 py-2 rounded-full mb-6 shadow-sm" data-testid="hero-badge">
            <Sparkles size={16} className="text-[#E65084]" />
            <span className="text-sm font-medium text-[#5E5358]">Handmade with love in the West Midlands</span>
          </div>

          <h1 className="font-heading text-5xl sm:text-6xl md:text-7xl font-bold leading-[1.05] tracking-tight text-[#2D2327]">
            Making Every{" "}
            <br className="hidden sm:block" />
            Celebration{" "}
            <span className="font-accent text-[#E65084] inline-block" style={{ fontSize: "0.95em" }}>
              Magical
            </span>
          </h1>

          <p className="mt-6 text-lg md:text-xl text-[#5E5358] leading-relaxed max-w-xl" data-testid="hero-tagline">
            Bespoke balloon arrangements and personalised party touches — created by Kate
            for birthdays, baby showers, gender reveals and every little reason to celebrate.
          </p>

          <div className="mt-9 flex flex-wrap gap-4" data-testid="hero-cta-row">
            <a href="#contact" className="btn-primary" data-testid="hero-cta-shop">
              <PartyPopper size={20} /> Get in Touch
            </a>
            <a href="#gallery" className="btn-secondary" data-testid="hero-cta-gallery">
              View Our Work <ArrowRight size={18} />
            </a>
          </div>

          <div className="mt-10 flex items-center gap-6 text-sm text-[#5E5358]">
            <div className="flex items-center gap-1">
              {Array.from({ length: 5 }).map((_, i) => (
                <StarIcon key={i} size={18} className="fill-[#FFC857] text-[#FFC857]" />
              ))}
            </div>
            <span>Loved by 200+ happy families</span>
          </div>
        </motion.div>

        {/* Visual */}
        <div className="lg:col-span-5 relative h-[420px] md:h-[520px]">
          {/* pastel blob backdrop */}
          <div className="absolute inset-6 bg-gradient-to-br from-[#FDE7F0] to-[#FFEFC9] blob" />
          {/* hero image */}
          <motion.img
            src={HERO_BALLOONS}
            alt="Floating pastel pink and gold balloons"
            data-testid="hero-balloons-image"
            className="absolute inset-0 w-full h-full object-contain drop-shadow-xl"
            animate={{ y: [0, -18, 0] }}
            transition={{ repeat: Infinity, duration: 4.5, ease: "easeInOut" }}
          />
          {/* small floating balloons overlay */}
          <Balloon color="#FF729F" highlight="#FFD7E3" size={62} className="absolute -left-4 top-10 float-med drop-shadow-md" />
          <Balloon color="#FFC857" highlight="#FFE9B6" size={48} className="absolute right-2 bottom-6 float-slow drop-shadow-md" />
        </div>
      </div>

      {/* bottom gentle wave */}
      <div className="absolute -bottom-1 left-0 right-0 pointer-events-none">
        <svg viewBox="0 0 1440 80" className="w-full" preserveAspectRatio="none">
          <path d="M0,40 C360,80 1080,0 1440,40 L1440,80 L0,80 Z" fill="#FFFBF9" />
        </svg>
      </div>
    </section>
  );
};

// ----------------- About -----------------
const About = () => (
  <section id="about" data-testid="about-section" className="relative py-24 md:py-32 bg-[#FFFBF9]">
    <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16 grid lg:grid-cols-12 gap-12 items-center">
      <motion.div
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, margin: "-80px" }}
        variants={fadeUp}
        className="lg:col-span-5 relative"
      >
        <div className="absolute -inset-4 bg-gradient-to-br from-[#FDE7F0] to-[#FFEFC9] blob z-0" />
        <img
          src={KATE_PHOTO}
          alt="Kate in front of one of her bespoke balloon arch creations"
          data-testid="about-image"
          className="relative z-10 w-full h-[460px] object-cover rounded-[2rem] shadow-xl"
        />
        <div className="absolute -bottom-6 -right-4 z-20 bg-white px-5 py-3 rounded-2xl shadow-lg flex items-center gap-2 border border-pink-100" data-testid="about-badge">
          <Heart size={18} className="text-[#E65084] fill-[#E65084]" />
          <span className="font-medium text-[#2D2327]">Handmade with love</span>
        </div>
      </motion.div>

      <motion.div
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, margin: "-80px" }}
        variants={fadeUp}
        className="lg:col-span-7"
      >
        <span className="font-accent text-[#E65084] text-3xl md:text-4xl">Meet Kate</span>
        <h2 className="font-heading text-4xl sm:text-5xl font-bold mt-2 text-[#2D2327] leading-tight">
          The lovely hands behind <br /> every little wonder.
        </h2>
        <p className="mt-6 text-lg text-[#5E5358] leading-relaxed">
          Hi, I&apos;m Kate — the heart, hands and helium behind Kate&apos;s Kreations. From my little
          studio in the West Midlands, I create balloon garlands, light-up letters, welcome signs
          and personalised party pieces that make every milestone feel utterly magical.
        </p>
        <p className="mt-4 text-lg text-[#5E5358] leading-relaxed">
          What started as making little touches for friends&apos; birthdays and gatherings quickly
          turned into a passion — and then into a proper labour of love. Today every order is
          still hand-crafted with the same care, because your celebration deserves a touch as
          unique as the moment itself.
        </p>

        <div className="mt-8 grid grid-cols-3 gap-4" data-testid="about-stats">
          {[
            { n: "500+", l: "Celebrations" },
            { n: "5★", l: "Avg. Review" },
            { n: "6 yrs", l: "Crafting Joy" },
          ].map((s) => (
            <div key={s.l} className="card-soft p-5 text-center">
              <div className="font-heading text-3xl font-bold text-[#E65084]">{s.n}</div>
              <div className="text-sm text-[#5E5358] mt-1">{s.l}</div>
            </div>
          ))}
        </div>
      </motion.div>
    </div>
  </section>
);

// ----------------- Services -----------------
const Services = () => {
  const services = [
    {
      title: "Balloon Arrangements",
      tag: "Our signature",
      desc: "Lush organic garlands, light-up letter & number frames, archways and table centerpieces in any colour palette you dream up.",
      points: ["Birthdays & milestones", "Baby showers", "Gender reveals", "Weddings & anniversaries"],
      icon: <PartyPopper size={26} className="text-white" />,
      tint: "#FF729F",
      tintSoft: "#FDE7F0",
      img: "https://images.unsplash.com/photo-1525268771113-32d9e9021a97?crop=entropy&cs=srgb&fm=jpg&q=85&w=900",
      testid: "service-balloons",
    },
    {
      title: "Personalised Printing",
      tag: "Add a sprinkle of magic",
      desc: "Custom name printing on children&apos;s cups, bottles, party bags and accessories — sweet keepsakes guests will treasure.",
      points: ["Kids' cups & bottles", "Party bags", "Welcome signs", "Favour labels"],
      icon: <Gift size={26} className="text-white" />,
      tint: "#FFC857",
      tintSoft: "#FFF1CF",
      img: "https://customer-assets.emergentagent.com/job_party-perfection-3/artifacts/z90s0h3g_600243206_710448175470974_2815216194968652249_n.jpg",
      testid: "service-printing",
    },
  ];

  return (
    <section id="services" data-testid="services-section" className="relative py-24 md:py-32 bg-[#FDF2F8]">
      <Confetti color="#FF729F" size={20} className="absolute left-[6%] top-12 drift hidden md:block" />
      <Star color="#FFC857" size={24} className="absolute right-[8%] top-20 confetti-spin hidden md:block" />

      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16">
        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} className="max-w-2xl">
          <span className="font-accent text-[#E65084] text-3xl md:text-4xl">What we do</span>
          <h2 className="font-heading text-4xl sm:text-5xl font-bold mt-2 text-[#2D2327] leading-tight">
            Two specialties, <br /> endless possibilities.
          </h2>
          <p className="mt-5 text-lg text-[#5E5358] leading-relaxed">
            From statement balloon installations to personalised party keepsakes — every piece is
            made bespoke for your celebration.
          </p>
        </motion.div>

        <div className="mt-14 grid md:grid-cols-2 gap-8">
          {services.map((s, idx) => (
            <motion.article
              key={s.title}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeUp}
              transition={{ delay: idx * 0.15 }}
              data-testid={s.testid}
              className="card-soft overflow-hidden group"
            >
              <div className="relative h-56 overflow-hidden">
                <img src={s.img} alt={s.title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" />
                <div
                  className="absolute top-4 left-4 w-12 h-12 rounded-2xl flex items-center justify-center shadow-lg"
                  style={{ background: s.tint }}
                >
                  {s.icon}
                </div>
                <div className="absolute top-4 right-4 px-3 py-1 rounded-full text-xs font-semibold text-white" style={{ background: s.tint }}>
                  {s.tag}
                </div>
              </div>
              <div className="p-8">
                <h3 className="font-heading text-2xl md:text-3xl font-bold text-[#2D2327]">{s.title}</h3>
                <p className="mt-3 text-[#5E5358] leading-relaxed" dangerouslySetInnerHTML={{ __html: s.desc }} />
                <ul className="mt-5 grid grid-cols-2 gap-2">
                  {s.points.map((p) => (
                    <li key={p} className="flex items-center gap-2 text-sm text-[#2D2327]">
                      <span className="w-2 h-2 rounded-full" style={{ background: s.tint }} />
                      {p}
                    </li>
                  ))}
                </ul>
                <a
                  href="#contact"
                  data-testid={`${s.testid}-cta`}
                  className="inline-flex items-center gap-2 mt-7 font-semibold"
                  style={{ color: s.tint }}
                >
                  Enquire about this <ArrowRight size={16} />
                </a>
              </div>
            </motion.article>
          ))}
        </div>
      </div>
    </section>
  );
};

// ----------------- Gallery -----------------
const Gallery = () => {
  const [filter, setFilter] = useState("all");
  const filtered = filter === "all" ? GALLERY : GALLERY.filter((g) => g.cat === filter);

  // Bento span pattern for 10 images
  const layout = [
    "md:col-span-2 md:row-span-2",
    "",
    "",
    "md:col-span-2",
    "",
    "",
    "md:col-span-2 md:row-span-2",
    "",
    "",
    "md:col-span-2",
  ];

  const filters = [
    { id: "all", label: "All Kreations" },
    { id: "balloons", label: "Balloons" },
    { id: "printing", label: "Personalised" },
  ];

  return (
    <section id="gallery" data-testid="gallery-section" className="relative py-24 md:py-32 bg-[#FFFBF9]">
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16">
        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} className="text-center max-w-2xl mx-auto">
          <span className="font-accent text-[#E65084] text-3xl md:text-4xl">Real moments</span>
          <h2 className="font-heading text-4xl sm:text-5xl font-bold mt-2 text-[#2D2327] leading-tight">
            A little peek at our recent kreations
          </h2>
          <p className="mt-5 text-lg text-[#5E5358] leading-relaxed">
            Birthdays, anniversaries, big days and small surprises — every one made magical.
          </p>
        </motion.div>

        <div className="mt-10 flex flex-wrap justify-center gap-3" data-testid="gallery-filters">
          {filters.map((f) => (
            <button
              key={f.id}
              onClick={() => setFilter(f.id)}
              data-testid={`gallery-filter-${f.id}`}
              className={`px-5 py-2.5 rounded-full text-sm font-semibold transition-all ${
                filter === f.id
                  ? "bg-[#FF729F] text-white shadow-md"
                  : "bg-white text-[#2D2327] border border-pink-100 hover:bg-[#FDF2F8]"
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>

        <div className="mt-10 grid grid-cols-2 md:grid-cols-4 auto-rows-[180px] md:auto-rows-[220px] gap-4">
          {filtered.map((g, i) => (
            <motion.div
              key={g.url}
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: (i % 4) * 0.08 }}
              data-testid={`gallery-image-${i + 1}`}
              className={`relative overflow-hidden rounded-3xl shadow-md group ${filter === "all" ? layout[i] || "" : ""}`}
            >
              <img
                src={g.url}
                alt={g.alt}
                className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#2D2327]/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
              <div className="absolute bottom-3 left-3 right-3 text-white text-sm opacity-0 group-hover:opacity-100 transition-opacity font-medium">
                {g.alt}
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

// ----------------- Testimonials -----------------
const Testimonials = () => {
  const reviews = [
    {
      name: "Sophie L.",
      role: "Birmingham · 30th birthday",
      quote:
        "Kate created the most stunning balloon garland for my 30th — guests couldn&apos;t stop taking photos with it! She made the whole process effortless.",
      accent: "#FF729F",
    },
    {
      name: "Emma & James",
      role: "Walsall · Baby shower",
      quote:
        "The personalised cups and balloon arch were absolute perfection. So much love and detail in every piece — exactly what we hoped for.",
      accent: "#FFC857",
    },
    {
      name: "Rebecca M.",
      role: "Wolverhampton · Daughter&apos;s 5th",
      quote:
        "From the personalised party bags to the light-up number — everything was magical. Lily was over the moon. Thank you Kate!",
      accent: "#E899AE",
    },
  ];

  return (
    <section id="testimonials" data-testid="testimonials-section" className="relative py-24 md:py-32 bg-gradient-to-b from-[#FDF2F8] to-[#FFFBF9]">
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16">
        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} className="text-center max-w-2xl mx-auto">
          <span className="font-accent text-[#E65084] text-3xl md:text-4xl">Kind words</span>
          <h2 className="font-heading text-4xl sm:text-5xl font-bold mt-2 text-[#2D2327] leading-tight">
            Loved by families across the Midlands
          </h2>
        </motion.div>

        <div className="mt-14 grid md:grid-cols-3 gap-6">
          {reviews.map((r, i) => (
            <motion.div
              key={r.name}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: i * 0.12 }}
              data-testid={`testimonial-${i + 1}`}
              className="card-soft p-8 border-t-4 relative"
              style={{ borderTopColor: r.accent }}
            >
              <div className="flex items-center gap-1 mb-4">
                {Array.from({ length: 5 }).map((_, k) => (
                  <StarIcon key={k} size={18} className="fill-[#FFC857] text-[#FFC857]" />
                ))}
              </div>
              <p className="text-[#2D2327] leading-relaxed text-lg" dangerouslySetInnerHTML={{ __html: `&ldquo;${r.quote}&rdquo;` }} />
              <div className="mt-6 pt-5 border-t border-pink-100">
                <div className="font-heading text-lg font-bold text-[#2D2327]">{r.name}</div>
                <div className="text-sm text-[#5E5358]" dangerouslySetInnerHTML={{ __html: r.role }} />
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

// ----------------- How to Order -----------------
const HowToOrder = () => {
  const steps = [
    { icon: <Search size={26} />, title: "Browse Inspiration", desc: "Have a peek at our gallery and dream up your perfect celebration palette.", tint: "#FF729F" },
    { icon: <MailOpen size={26} />, title: "Contact Kate", desc: "Drop us a WhatsApp or email with your date, theme and ideas — we&apos;ll do the rest.", tint: "#FFC857" },
    { icon: <Smile size={26} />, title: "Enjoy the Magic!", desc: "Sit back while we deliver and set up your balloons and personalised pieces.", tint: "#E899AE" },
  ];

  return (
    <section id="order" data-testid="order-section" className="relative py-24 md:py-32 bg-[#FFFBF9]">
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16">
        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} className="text-center max-w-2xl mx-auto">
          <span className="font-accent text-[#E65084] text-3xl md:text-4xl">How it works</span>
          <h2 className="font-heading text-4xl sm:text-5xl font-bold mt-2 text-[#2D2327] leading-tight">
            Three little steps to your celebration
          </h2>
        </motion.div>

        <div className="mt-16 grid md:grid-cols-3 gap-6 relative">
          {/* connecting line */}
          <div className="hidden md:block absolute top-12 left-[16%] right-[16%] h-0.5 bg-gradient-to-r from-[#FF729F] via-[#FFC857] to-[#E899AE] opacity-30" />
          {steps.map((s, i) => (
            <motion.div
              key={s.title}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: i * 0.15 }}
              data-testid={`step-${i + 1}`}
              className="card-soft p-8 text-center relative"
            >
              <div
                className="relative z-10 w-16 h-16 rounded-2xl mx-auto flex items-center justify-center text-white shadow-lg"
                style={{ background: s.tint }}
              >
                {s.icon}
              </div>
              <div className="mt-4 inline-block font-accent text-2xl" style={{ color: s.tint }}>
                Step {i + 1}
              </div>
              <h3 className="font-heading text-2xl font-bold mt-1 text-[#2D2327]">{s.title}</h3>
              <p className="mt-3 text-[#5E5358] leading-relaxed" dangerouslySetInnerHTML={{ __html: s.desc }} />
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

// ----------------- FAQ -----------------
const FAQ_ITEMS = [
  {
    q: "How far in advance should I book?",
    a: "We recommend booking at least 2–4 weeks ahead — especially for weekends and busy seasons like Christmas and summer. For larger setups (light-up letters, big garlands), 4–6 weeks is ideal. Need something last-minute? Drop us a message — we'll always try our best to make it happen.",
  },
  {
    q: "What areas do you cover?",
    a: "We're based in the West Midlands and deliver locally across the region — including Birmingham, Wolverhampton, Walsall, Dudley and surrounding areas. Travel further afield can be arranged for a small fee. Personalised printing items can be posted anywhere in the UK.",
  },
  {
    q: "Do you deliver and set up the balloons?",
    a: "Yes! Every balloon arrangement and light-up display is hand-delivered and set up by Kate, so it looks absolutely perfect for your big moment. Collection of any hire items (like light-up numbers) is included too.",
  },
  {
    q: "How much do your balloon arrangements cost?",
    a: "Prices start from around £45 for smaller garlands and table arrangements, with larger statement installations and light-up letter packages starting from £150. Every quote is bespoke based on size, colours and setup — message us with your ideas and we'll send a friendly, no-obligation price.",
  },
  {
    q: "Can I choose my own colours and theme?",
    a: "Absolutely — that's the whole point! Every arrangement is custom-made to your colour palette, theme and style. Whether you want pastel pink & gold, neon brights, monochrome or something completely unique, just share your vision.",
  },
  {
    q: "How long do balloon garlands last?",
    a: "Our balloon garlands are made with high-quality latex and are designed to look beautiful for 2–5 days indoors. Foil balloons can last even longer. We don't recommend outdoor setups in direct sunlight or wind.",
  },
  {
    q: "What can be personalised?",
    a: "Lots! Children's cups, water bottles, party bags, welcome signs, gift boxes, hampers, glass tumblers, sports gloves, baby gifts and party favours — if it has a smooth surface, chances are we can pop a name, initial or design on it.",
  },
  {
    q: "Is there a minimum order for personalised items?",
    a: "No minimum — whether you need a single keepsake cup or 30 party-bag tumblers for a class birthday, we'd love to help. Bulk orders may qualify for a small discount.",
  },
  {
    q: "Can I supply my own items to be personalised?",
    a: "Yes, in most cases! Just send a photo and we'll confirm whether the material works with our printing. Please make sure items are clean and unused.",
  },
  {
    q: "How do I pay and is a deposit needed?",
    a: "A 25% non-refundable deposit secures your date, with the balance due 7 days before your celebration. Payment is by bank transfer or PayPal. Personalised printing orders are typically paid in full at the time of order.",
  },
];

const FAQ = () => (
  <section id="faq" data-testid="faq-section" className="relative py-24 md:py-32 bg-[#FDF2F8]">
    <Star color="#FFC857" size={22} className="absolute right-[8%] top-12 confetti-spin hidden md:block" />
    <Confetti color="#FF729F" size={22} className="absolute left-[6%] bottom-16 drift hidden md:block" />

    <div className="max-w-4xl mx-auto px-6 md:px-12">
      <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} className="text-center max-w-2xl mx-auto">
        <span className="font-accent text-[#E65084] text-3xl md:text-4xl">Good to know</span>
        <h2 className="font-heading text-4xl sm:text-5xl font-bold mt-2 text-[#2D2327] leading-tight">
          Frequently asked questions
        </h2>
        <p className="mt-5 text-lg text-[#5E5358] leading-relaxed">
          Everything you might be wondering before you book — and if it&apos;s not here, just ask Kate directly.
        </p>
      </motion.div>

      <motion.div
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true }}
        variants={fadeUp}
        className="mt-12 bg-white rounded-3xl shadow-[0_8px_30px_rgba(255,114,159,0.08)] p-4 md:p-8"
        data-testid="faq-accordion-wrap"
      >
        <Accordion type="single" collapsible className="w-full" data-testid="faq-accordion">
          {FAQ_ITEMS.map((item, i) => (
            <AccordionItem
              key={i}
              value={`item-${i}`}
              data-testid={`faq-item-${i + 1}`}
              className="border-b border-pink-100 last:border-b-0"
            >
              <AccordionTrigger className="!py-5 !text-left hover:!no-underline group">
                <span className="flex items-start gap-3 pr-4">
                  <span className="mt-0.5 flex-shrink-0 w-7 h-7 rounded-full bg-[#FDE7F0] text-[#E65084] flex items-center justify-center text-xs font-bold font-heading">
                    {i + 1}
                  </span>
                  <span className="font-heading text-lg md:text-xl font-semibold text-[#2D2327] group-hover:text-[#E65084] transition-colors">
                    {item.q}
                  </span>
                </span>
              </AccordionTrigger>
              <AccordionContent className="pl-10 pr-2 pb-5 text-[#5E5358] text-base leading-relaxed">
                {item.a}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </motion.div>

      <motion.div
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true }}
        variants={fadeUp}
        className="mt-10 text-center"
      >
        <p className="text-[#5E5358] inline-flex items-center gap-2">
          <HelpCircle size={18} className="text-[#E65084]" />
          Still got a question? <a href="#contact" data-testid="faq-ask-link" className="font-semibold text-[#E65084] hover:underline">Ask Kate directly</a>
        </p>
      </motion.div>
    </div>
  </section>
);

// ----------------- Contact -----------------
const Contact = () => (
  <section id="contact" data-testid="contact-section" className="relative py-24 md:py-32 bg-gradient-to-br from-[#FDE7F0] via-[#FFFBF9] to-[#FFF1CF] overflow-hidden">
    <Balloon color="#FF729F" highlight="#FFD7E3" size={70} className="absolute left-8 top-16 float-slow opacity-80 hidden md:block" />
    <Balloon color="#FFC857" highlight="#FFE9B6" size={56} className="absolute right-12 bottom-20 float-fast opacity-90 hidden md:block" />
    <Confetti color="#E65084" size={22} className="absolute left-1/4 bottom-10 drift hidden md:block" />
    <Star color="#FFC857" size={20} className="absolute right-1/3 top-10 confetti-spin hidden md:block" />

    <div className="max-w-5xl mx-auto px-6 md:px-12 text-center relative z-10">
      <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp}>
        <span className="font-accent text-[#E65084] text-3xl md:text-4xl">Let&apos;s celebrate</span>
        <h2 className="font-heading text-4xl sm:text-5xl md:text-6xl font-bold mt-2 text-[#2D2327] leading-tight">
          Ready to make it magical?
        </h2>
        <p className="mt-6 text-lg md:text-xl text-[#5E5358] max-w-2xl mx-auto leading-relaxed">
          Get in touch with Kate — we&apos;d love to hear about your celebration and put together
          something beautiful, just for you.
        </p>

        <div className="mt-10 flex flex-wrap gap-4 justify-center" data-testid="contact-cta-row">
          <a
            href={WHATSAPP_LINK}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="contact-whatsapp-button"
            className="btn-primary"
          >
            <MessageCircle size={20} /> WhatsApp Kate
          </a>
          <a href={MAILTO} data-testid="contact-email-button" className="btn-secondary">
            <Mail size={20} /> Send an Email
          </a>
        </div>

        <div className="mt-12 grid sm:grid-cols-3 gap-4 max-w-3xl mx-auto" data-testid="contact-info-grid">
          <div className="card-soft p-6 flex items-center gap-3 justify-center">
            <Phone size={20} className="text-[#E65084]" />
            <span className="text-[#2D2327] font-medium">{PHONE_DISPLAY}</span>
          </div>
          <div className="card-soft p-6 flex items-center gap-3 justify-center">
            <Mail size={20} className="text-[#E65084]" />
            <span className="text-[#2D2327] font-medium text-sm">{EMAIL}</span>
          </div>
          <div className="card-soft p-6 flex items-center gap-3 justify-center">
            <MapPin size={20} className="text-[#E65084]" />
            <span className="text-[#2D2327] font-medium">West Midlands, UK</span>
          </div>
        </div>
      </motion.div>
    </div>
  </section>
);

// ----------------- Footer -----------------
const Footer = () => (
  <footer data-testid="site-footer" className="bg-[#2D2327] text-white relative overflow-hidden">
    <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16 py-16 grid md:grid-cols-3 gap-10">
      <div>
        <img
          src={LOGO}
          alt="Kate's Kreations — Unique and Personalised Gifts"
          data-testid="footer-logo"
          className="h-44 md:h-52 w-auto -ml-3"
          loading="lazy"
        />
        <p className="mt-2 text-white/70 leading-relaxed max-w-xs">
          Bespoke balloon arrangements and personalised party touches, handmade with love in the West Midlands.
        </p>
      </div>

      <div>
        <h4 className="font-heading text-lg font-semibold mb-4 text-[#FFC857]">Quick Links</h4>
        <ul className="space-y-2 text-white/80">
          {[
            { href: "#about", l: "About Kate" },
            { href: "#services", l: "Services" },
            { href: "#gallery", l: "Gallery" },
            { href: "#order", l: "How to Order" },
            { href: "#faq", l: "FAQ" },
            { href: "#contact", l: "Contact" },
          ].map((x) => (
            <li key={x.href}>
              <a href={x.href} data-testid={`footer-link-${x.l.toLowerCase().replace(/\s/g, "-")}`} className="hover:text-[#FF729F] transition-colors">
                {x.l}
              </a>
            </li>
          ))}
        </ul>
      </div>

      <div>
        <h4 className="font-heading text-lg font-semibold mb-4 text-[#FFC857]">Stay Connected</h4>
        <p className="text-white/70 mb-4">Follow along for fresh inspiration and special offers.</p>
        <div className="flex items-center gap-3" data-testid="footer-socials">
          <a href="#" data-testid="social-facebook" aria-label="Facebook" className="w-11 h-11 rounded-full bg-white/10 hover:bg-[#FF729F] flex items-center justify-center transition-colors">
            <Facebook size={20} />
          </a>
          <a href="#" data-testid="social-instagram" aria-label="Instagram" className="w-11 h-11 rounded-full bg-white/10 hover:bg-[#FF729F] flex items-center justify-center transition-colors">
            <Instagram size={20} />
          </a>
          <a href={WHATSAPP_LINK} target="_blank" rel="noopener noreferrer" data-testid="social-whatsapp" aria-label="WhatsApp" className="w-11 h-11 rounded-full bg-white/10 hover:bg-[#FF729F] flex items-center justify-center transition-colors">
            <MessageCircle size={20} />
          </a>
          <a href={MAILTO} data-testid="social-email" aria-label="Email" className="w-11 h-11 rounded-full bg-white/10 hover:bg-[#FF729F] flex items-center justify-center transition-colors">
            <Mail size={20} />
          </a>
        </div>
      </div>
    </div>

    <div className="border-t border-white/10">
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-6 flex flex-col sm:flex-row items-center justify-between gap-3 text-sm text-white/60">
        <span>© {new Date().getFullYear()} Kate&apos;s Kreations. All celebrations reserved.</span>
        <span className="flex items-center gap-1.5">
          Made with <Heart size={14} className="fill-[#FF729F] text-[#FF729F]" /> in the West Midlands
        </span>
      </div>
    </div>
  </footer>
);

// ----------------- App -----------------
function App() {
  return (
    <div className="App" data-testid="app-root">
      <Header />
      <main>
        <Hero />
        <About />
        <Services />
        <Gallery />
        <Testimonials />
        <HowToOrder />
        <FAQ />
        <Contact />
      </main>
      <Footer />
    </div>
  );
}

export default App;
